import { get } from 'lodash';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';

import { handleApiErrors } from '../../../../api/axiosInstance';
import {
  approveReceipt,
  RECEIPT_API,
  rejectReceipt,
  rejectTripsInReceipt,
} from '../../../../api/receipt';
import {
  createReceiptReview,
  resubmitReceiptForApproval,
} from '../../../../api/trip-receipt-reviews';
import Toast from '../../../../components/Toast';
import { INTERNAL_LINKS, RECEIPT_TYPES, STATUS_LIST } from '../../../../enum';
import useRetryMassTransactionMutation from '../../../../hooks/mutations/useRetryMassTransactionMutation';
import usePaginatedFiltersQuery from '../../../../hooks/queries/usePaginatedFiltersQuery';
import useModal from '../../../../hooks/useModal';
import useTableSort from '../../../../hooks/useTableSort';
import { normalizeTripsSchema } from '../../../../utils/trips';

const useReceiptDetailsState = ({ t, history, receiptId, searchTerm }) => {
  const queryClient = useQueryClient();
  const getReceiptQuery = useQuery({
    placeholderData: {},
    enabled: !!receiptId,
    queryKey: ['getReceiptQuery', receiptId],
    queryFn: () => RECEIPT_API.fetchTripReceipt(receiptId),
  });
  const receipt = getReceiptQuery.data;
  const companySettings = useSelector(store => store.common.currentCompany.companySettingId);
  const { tableSort, handleTableSort } = useTableSort({
    journeyStartTs: -1,
  });
  const {
    query: getReceiptTripsQuery,
    paginationConfig,
    handlePageChange,
    handlePageSizeChange,
  } = usePaginatedFiltersQuery({
    enabled: !!receiptId,
    queryKey: ['getReceiptTripsQuery', receiptId, JSON.stringify(tableSort), searchTerm],
    staleTime: Infinity,
    placeholderData: { documents: [], totalCount: 0, moreInfoCount: 0 },
    onError: error => handleApiErrors(error.response),
    queryFn: async () => {
      const response = await RECEIPT_API.fetchTripReceiptTrips({
        receiptId,
        pageNumber: paginationConfig.current,
        pageSize: paginationConfig.pageSize,
        sort: tableSort,
        searchTerm,
      });
      return {
        documents: get(response, 'documents', []).map(trp => normalizeTripsSchema(trp, undefined)),
        totalCount: get(response, 'totalCount', 0),
        moreInfoCount: get(response, 'moreInfoCount', 0),
      };
    },
  });

  let receiptTrips = getReceiptTripsQuery.data.documents;
  const moreInfoTripsCount = getReceiptTripsQuery.data.moreInfoCount;
  const totalTripsCount = getReceiptTripsQuery.data.totalCount;
  const [selectedTrips, setSelectedTrips] = useState([]);
  const [tripIdToDeny, setTripIdToDeny] = useState();

  const [isTripDetailsVisible, setIsTripDetailsVisible] = useState(false);
  const [isMonthlyChartVisible, setIsMonthlyChartVisible] = useState(false);
  const [tripMapConfig, setTripMapConfig] = useState({
    center: [0, 0],
    routes: [],
  });

  const [isAdjustmentHistVisible, setIsAdjustmentHistVisible] = useState(false);
  const [isStatusHistoryVisible, setIsStatusHistoryVisible] = useState(false);
  const [isSubmissionHistoryVisible, setIsSubmissionHistoryVisible] = useState(false);
  const [isPaymentStatusHistoryVisible, setIsPaymentStatusHistoryVisible] = useState(false);

  const [isRequestingMoreInfo, setIsRequestingMoreInfo] = useState(false);
  const [isProcessingMoreInfoRequest, setIsProcessingMoreInfoRequest] = useState(false);
  const [selectedTripsMoreInfo, setSelectedTripsMoreInfo] = useState([]);

  const [isApprovalConfirmationVisible, setIsApprovalConfirmationVisible] = useState(false);
  const [isApprovalWithMoreInfoVisible, setIsApprovalWithMoreInfoVisible] = useState(false);
  const [isRejectConfirmationVisible, setIsRejectConfirmationVisible] = useState(false);
  const [isTripRejectConfirmationVisible, setIsTripRejectConfirmationVisible] = useState(false);
  const [isResubmitConfirmationVisible, setIsResubmitConfirmationVisible] = useState(false);

  const [isApprovingReceipt, setIsApprovingReceipt] = useState(false);
  const [isRejectingReceipt, setIsRejectingReceipt] = useState(false);
  const [isRejectingTrip, setIsRejectingTrip] = useState(false);
  const [isResubmittingReceiptsForApproval, setIsResubmittingReceiptsForApproval] = useState(false);

  const [isDeleteReceiptModalVisible, openDeleteReceiptModal, closeDeleteReceiptModal] = useModal();

  const openApprovalConfirmation = () => {
    if (moreInfoTripsCount) {
      setIsApprovalWithMoreInfoVisible(true);
    } else {
      setIsApprovalConfirmationVisible(true);
    }
  };

  const closeApprovalConfirmation = () => {
    setIsApprovalConfirmationVisible(false);
    setIsApprovalWithMoreInfoVisible(false);
  };

  const openRejectionConfirmation = () => setIsRejectConfirmationVisible(true);
  const closeRejectionConfirmation = () => setIsRejectConfirmationVisible(false);

  const openTripRejectionConfirmation = tripID => {
    setTripIdToDeny(tripID);
    setIsTripRejectConfirmationVisible(true);
  };

  const closeTripRejectionConfirmation = () => {
    setTripIdToDeny();
    setIsTripRejectConfirmationVisible(false);
  };

  const openResubmitConfirmation = () => setIsResubmitConfirmationVisible(true);
  const closeResubmitConfirmation = () => setIsResubmitConfirmationVisible(false);

  const handleTripSelection = selectedRowKeys => setSelectedTrips(selectedRowKeys);

  const toggleAdjustmentHistoryModal = () => {
    setIsAdjustmentHistVisible(!isAdjustmentHistVisible);
  };

  const toggleStatusHistoryModal = () => {
    setIsStatusHistoryVisible(!isStatusHistoryVisible);
  };

  const toggleSubmissionHistoryModal = () => {
    setIsSubmissionHistoryVisible(visible => !visible);
  };

  const togglePaymentStatusHistoryModal = () => {
    setIsPaymentStatusHistoryVisible(!isPaymentStatusHistoryVisible);
  };

  const toggleRequestMoreInfo = () => {
    setSelectedTripsMoreInfo(isRequestingMoreInfo ? [] : selectedTripsMoreInfo);
    setIsRequestingMoreInfo(!isRequestingMoreInfo);
  };

  const updateSelectedTripsForMoreInfo = tripIds => setSelectedTripsMoreInfo(tripIds);

  const _redirectOnFinish = () => {
    if (companySettings?.settings?.hideReceipts) {
      history.replace(INTERNAL_LINKS.REIMBURSEMENT);
    } else {
      history.replace(INTERNAL_LINKS.RECEIPTS);
    }
  };

  const handleMoreInfoRequest = async (tripReceiptId, tripsIds, comment) => {
    setIsProcessingMoreInfoRequest(true);

    try {
      await createReceiptReview(tripReceiptId, tripsIds, comment);
      Toast({
        type: 'success',
        message: t('moreInfoSubmitSuccess'),
      });
      toggleRequestMoreInfo();
      invalidateReceiptQueries();
    } catch (error) {
      handleApiErrors(error.response, () => {
        Toast({
          type: 'error',
          message: t('errorRequestingMoreInfo'),
        });
      });
    }

    setIsProcessingMoreInfoRequest(false);
  };

  const handleResubmitForApproval = async tripReceiptId => {
    setIsResubmittingReceiptsForApproval(true);

    try {
      await resubmitReceiptForApproval([tripReceiptId]);
      closeResubmitConfirmation();
      invalidateReceiptQueries();
      Toast({
        type: 'success',
        message: t('resubmitTripReceiptForApproval'),
      });
    } catch (error) {
      handleApiErrors(error.response, () => {
        Toast({
          type: 'error',
          message: t('errorResubmitTripReceiptForApproval'),
        });
      });
    }

    setIsResubmittingReceiptsForApproval(false);
  };

  const handleViewTripMapFullScreen = (trp, activeRoutes, mapCenter) => {
    setIsTripDetailsVisible(true);
    setTripMapConfig({
      tripId: trp._id,
      isManual: !trp.gpsVerified || trp.gpsVerified === 'No',
      toLoc: trp.to_loc,
      fromLoc: trp.from_loc,
      routes: activeRoutes,
      center: mapCenter,
    });
  };

  const handleTripMapClose = () => {
    setIsTripDetailsVisible(false);
    setTripMapConfig({ center: [0, 0], routes: [] });
  };

  const invalidateReceiptQueries = () => {
    queryClient.invalidateQueries(['getReceiptQuery'], { exact: false });
    queryClient.invalidateQueries(['getReceiptTripsQuery'], { exact: false });
  };

  const handleApprove = async (id, approveAction) => {
    setIsApprovingReceipt(true);

    const tripsAwaitingApproval = receiptTrips.filter(
      trip => trip.journeyStatus === STATUS_LIST().Status.SUBMITTED,
    );

    if (approveAction === 'approvePending' && !tripsAwaitingApproval.length) {
      return Toast({
        type: 'error',
        message: t('noTripsAwaitingForApproval'),
      });
    }

    try {
      const response = await approveReceipt([id], approveAction);

      if (receipt.type === RECEIPT_TYPES.FIXED_TYPE) {
        Toast({
          type: 'success',
          message: t('receiptApprovedSuccesfully'),
        });
      } else if (response?.message) {
        Toast({
          type: 'success',
          message: response.message,
        });
      } else {
        Toast({
          type: 'success',
          message: t('receiptApprovedSuccesfully'),
        });
      }

      setSelectedTrips([]);
      closeApprovalConfirmation();
      invalidateReceiptQueries();
    } catch (error) {
      handleApiErrors(error.response, () => {
        Toast({
          type: 'error',
          message: t('approveReceiptError'),
        });
      });
    }

    setIsApprovingReceipt(false);
  };

  const handleDeny = async id => {
    setIsRejectingReceipt(true);

    try {
      await rejectReceipt([id]);

      _redirectOnFinish();

      if (receipt.type === RECEIPT_TYPES.FIXED_TYPE) {
        Toast({
          type: 'success',
          message: t('receiptDeniedSuccesfully'),
        });
      } else {
        Toast({
          type: 'success',
          message: t('tripsWereRejected', { count: receiptTrips.length }),
        });
      }
      return;
    } catch (error) {
      handleApiErrors(error.response, () => {
        Toast({
          type: 'error',
          message: t('errorWhileDenyingReceipt'),
        });
      });
    }

    setIsRejectingReceipt(false);
  };

  const handleTripDeny = async () => {
    setIsRejectingTrip(true);

    try {
      await rejectTripsInReceipt(receipt._id, [tripIdToDeny]);
      closeTripRejectionConfirmation();

      if (receiptTrips.length === 1) {
        // If there's only one trip, redirect to list page as it will be deleted
        _redirectOnFinish();
      } else {
        invalidateReceiptQueries();
      }

      Toast({
        type: 'success',
        message: t('tripsWereRejected', { count: 1 }),
      });
    } catch (error) {
      handleApiErrors(error.response, () => {
        Toast({
          type: 'error',
          message: t('errorWhileDenyingReceipt'),
        });
      });
    }

    setIsRejectingTrip(false);
  };

  const updateTripLatestComment = () => {
    queryClient.invalidateQueries(['getReceiptTripsQuery'], { exact: false });
  };

  const deleteReceiptMutation = useMutation({
    mutationFn: () => RECEIPT_API.deleteReceipt(receiptId),
    onSuccess: () => _redirectOnFinish(),
    onError: error => handleApiErrors(error.response),
  });

  const retryMassTransactionMutation = useRetryMassTransactionMutation(t, {
    onSuccess: () => {
      invalidateReceiptQueries();
    },
  });

  return {
    // Booleans
    isReceiptLoading: getReceiptQuery.isFetching,
    isApprovingReceipt,
    isRejectingTrip,
    isRejectingReceipt,
    isTripDetailsVisible,
    isAdjustmentHistVisible,
    isStatusHistoryVisible,
    isPaymentStatusHistoryVisible,
    isRequestingMoreInfo,
    isProcessingMoreInfoRequest,
    isResubmittingReceiptsForApproval,
    isApprovalConfirmationVisible,
    isRejectConfirmationVisible,
    isTripRejectConfirmationVisible,
    isApprovalWithMoreInfoVisible,
    isResubmitConfirmationVisible,
    isDeleteReceiptModalVisible,
    isMonthlyChartVisible,
    isSubmissionHistoryVisible,
    // General variables
    receipt,
    receiptTrips,
    selectedTrips,
    tripMapConfig,
    selectedTripsMoreInfo,
    moreInfoTripsCount,
    totalTripsCount,
    paginationConfig,
    // Handlers & Togglers
    //updateReceiptData,
    handleTripSelection,
    handleViewTripMapFullScreen,
    handleTripMapClose,
    handleMoreInfoRequest,
    handleResubmitForApproval,
    handleApprove,
    handleDeny,
    handleTripDeny,
    toggleAdjustmentHistoryModal,
    toggleStatusHistoryModal,
    togglePaymentStatusHistoryModal,
    toggleSubmissionHistoryModal,
    toggleRequestMoreInfo,
    openApprovalConfirmation,
    closeApprovalConfirmation,
    openRejectionConfirmation,
    closeRejectionConfirmation,
    openTripRejectionConfirmation,
    closeTripRejectionConfirmation,
    openResubmitConfirmation,
    closeResubmitConfirmation,
    updateSelectedTripsForMoreInfo,
    setIsMonthlyChartVisible,
    handlePageChange,
    handleTableSort,
    handlePageSizeChange,
    updateTripLatestComment,
    openDeleteReceiptModal,
    closeDeleteReceiptModal,
    deleteReceiptMutation,
    retryMassTransactionMutation,
  };
};

export default useReceiptDetailsState;
